import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Phone, MapPin, Package, CheckCircle, Clock, MessageCircle, Navigation, FileText, Download, Loader2, Banknote, Share2, Trash2, CreditCard, Wallet, Building2, Coins, ArrowLeft, Send, Save, Edit3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SHIPPING_REWARD_PER_DELIVERY = 40; // ש"ח לכל משלוח
const CONFIRM_DELAY_SECONDS = 20;

// Team members unified to the same look as Yaniv per user request —
// "שכל האפליקציות יהיו כמו יניב ... אותו דבר בדיוק". All three courier
// apps now share the indigo header + identical layout; only name/phone
// differ so the app knows whose orders to fetch.
const STAFF_MAP = {
  liran: { name: 'לירן', methods: ['sent_liran'], color: '#4f46e5', bg: 'from-indigo-600 to-indigo-700', phone: '0509959513' },
  yaniv: { name: 'יניב', methods: ['sent_yaniv'], color: '#4f46e5', bg: 'from-indigo-600 to-indigo-700', phone: '0515112205' },
  bari: { name: 'ברי', methods: ['private', 'sent_bari'], color: '#4f46e5', bg: 'from-indigo-600 to-indigo-700', phone: '0539406051' },
  // Daniel — added May 2026 by user request. Identical layout/feature
  // set to Yaniv. His phone is left blank until provided; keeps the
  // route working before then.
  daniel: { name: 'דניאל', methods: ['sent_daniel'], color: '#4f46e5', bg: 'from-indigo-600 to-indigo-700', phone: '0532260855' },
};

const STOCK_PACK_SIZE = 6;
const BOSS_PHONE = '0539406051';

// Named per-product stock (Oct 2026) — the three products couriers carry.
// `match` detects the product inside an order item's name so completed
// deliveries reduce the right counter automatically.
const STOCK_PRODUCTS = [
  { key: '5in1', label: '5 IN 1', match: (n) => n.includes('5 in 1') || n.includes('5in1') },
  { key: 'spray', label: 'ספריי השהייה', match: (n) => n.includes('ספריי') || n.includes('promescent') || n.includes('השהי') },
];

const normalizePhone = (p) => (p || '').replace(/[-\s]/g, '').replace(/^972/, '').replace(/^0/, '');

const TeamApp = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const staff = STAFF_MAP[memberId?.toLowerCase()];
  const [successPopup, setSuccessPopup] = useState(false);

  // Dynamically set manifest based on member so PWA install uses correct start_url
  useEffect(() => {
    if (!memberId) return;
    const id = memberId.toLowerCase();
    if (!STAFF_MAP[id]) return;
    const manifestUrl = `${API}/team/manifest?member=${id}`;
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    const prev = link.getAttribute('href');
    link.setAttribute('href', manifestUrl);
    // Apple touch icon + theme color
    let apple = document.querySelector('link[rel="apple-touch-icon"]');
    if (!apple) {
      apple = document.createElement('link');
      apple.rel = 'apple-touch-icon';
      document.head.appendChild(apple);
    }
    apple.setAttribute('href', 'https://customer-assets.emergentagent.com/job_d56f6086-1f6c-43f9-a07d-5d56a502df97/artifacts/sllsqwqk_%D7%A2%D7%99%D7%A6%D7%95%D7%91%20%D7%9C%D7%9C%D7%90%20%D7%A9%D7%9D%20%2824%29.png');
    let meta = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'apple-mobile-web-app-capable');
      meta.setAttribute('content', 'yes');
      document.head.appendChild(meta);
    }
    return () => {
      if (prev) link.setAttribute('href', prev);
    };
  }, [memberId]);

  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [view, setView] = useState('open');
  const [loading, setLoading] = useState(true);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [showIOSInstall, setShowIOSInstall] = useState(false);
  const [showHowToInstall, setShowHowToInstall] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  // ShipmentPickerModal = "בחר איזה משלוח לשלם" — lets the courier pick
  // individual shipments to settle, or "שלם הכל" at once. Clicking either
  // opens the SettlementModal pre-scoped to that subset.
  const [showShipmentPicker, setShowShipmentPicker] = useState(false);
  // Orders selected for the current settlement session (subset of
  // completedOrders). Empty → fallback to all completedOrders.
  const [settleTargetOrders, setSettleTargetOrders] = useState([]);
  // Bonus system — SERVER-SIDE (db.courier_bonus): every 10 completed
  // deliveries → +100₪ accumulated bonus, counter resets to 0 and climbs
  // again. Reverting a delivery rolls the calculation back (even across a
  // milestone). The bonus is deducted from "סה"כ לבריאות הגבר" and consumed
  // on settlement. Bari excluded (per spec).
  const [bonusCounter, setBonusCounter] = useState(0);
  const [bonusEarned, setBonusEarned] = useState(0);
  const [showBonusPopup, setShowBonusPopup] = useState(false);
  useEffect(() => {
    if (!memberId || memberId === 'bari') return;
    axios.get(`${API}/team/bonus/${memberId}`)
      .then(r => { setBonusCounter(r.data.counter || 0); setBonusEarned(r.data.bonus_total || 0); })
      .catch(() => {});
  }, [memberId]);
  const incrementBonusCounter = async () => {
    if (memberId === 'bari') return;
    try {
      const r = await axios.post(`${API}/team/bonus/${memberId}/increment`);
      setBonusCounter(r.data.counter || 0);
      setBonusEarned(r.data.bonus_total || 0);
      if (r.data.awarded) setShowBonusPopup(true);
    } catch (_e) {}
  };
  const decrementBonusCounter = async () => {
    if (memberId === 'bari') return;
    try {
      const r = await axios.post(`${API}/team/bonus/${memberId}/decrement`);
      setBonusCounter(r.data.counter || 0);
      setBonusEarned(r.data.bonus_total || 0);
    } catch (_e) {}
  };
  const consumeBonus = async () => {
    if (memberId === 'bari') return;
    try {
      const r = await axios.post(`${API}/team/bonus/${memberId}/consume`);
      setBonusEarned(r.data?.bonus_total || 0);
    } catch (_e) {}
  };

  const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const knownIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const fetchOrders = useCallback(async () => {
    if (!staff) return;
    try {
      const { data } = await axios.get(`${API}/orders`);
      setAllOrders(data || []);
      const mine = (data || []).filter(
        (o) => staff.methods.includes(o.payment_method) || o.sent_by === staff.name
      );
      if (!firstLoadRef.current) {
        const newOnes = mine.filter(
          (o) => !knownIdsRef.current.has(o.id) && o.status !== 'completed'
        );
        newOnes.forEach((o) => showLocalNotification(o));
      }
      knownIdsRef.current = new Set(mine.map((o) => o.id));
      firstLoadRef.current = false;
      setOrders(mine);
    } catch (e) {
      console.error('fetchOrders', e);
    }
    setLoading(false);
  }, [staff]);

  useEffect(() => {
    fetchOrders();
    const tick = setInterval(fetchOrders, 15000);
    return () => clearInterval(tick);
  }, [fetchOrders]);

  // Load payment-providers config once for the settlement flow
  useEffect(() => {
    axios.get(`${API}/payment-providers/config`).then(({ data }) => setPaymentConfig(data)).catch(() => {});
  }, []);

  useEffect(() => {
    // Register fresh service worker + force update check - SCOPED to /team only
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', { scope: '/team/' }).then((reg) => {
        reg.update().catch(() => {});
        if (reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      }).catch((err) => console.error('SW', err));
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });
    }
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    // iOS detection (no beforeinstallprompt on iOS)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isIOS && !isStandalone) setShowIOSInstall(true);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const showLocalNotification = (order) => {
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        const n = new Notification('משלוח חדש!', {
          body: `${order.customer_name} - ${order.total_amount} ש"ח\n${order.customer_address || ''}`,
          icon: 'https://customer-assets.emergentagent.com/job_d56f6086-1f6c-43f9-a07d-5d56a502df97/artifacts/sllsqwqk_%D7%A2%D7%99%D7%A6%D7%95%D7%91%20%D7%9C%D7%9C%D7%90%20%D7%A9%D7%9D%20%2824%29.png',
          tag: `order-${order.id}`,
        });
        n.onclick = () => window.focus();
      }
    } catch (_e) {}
  };

  const installApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
    setCanInstall(false);
  };

  const markCompleted = async (order) => {
    try {
      await axios.put(`${API}/admin/orders/${order.id}/status`, { status: 'completed' });
      setSuccessPopup(true);
      setTimeout(() => setSuccessPopup(false), 2500);
      // Bonus tracker — each successful delivery counts toward the next
      // 100₪ milestone (every 10 deliveries). Bari is excluded inside the
      // helper itself.
      incrementBonusCounter();
      fetchOrders();
    } catch (e) {
      alert('שגיאה בעדכון: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Revert: completed → open. Used when the courier accidentally tapped
  // "נמסר" before actually delivering. Status goes back to 'processing'
  // and the card returns to the "פתוחים" tab.
  const revertToOpen = async (order) => {
    const ok = window.confirm(`להחזיר את ההזמנה של ${order.customer_name || ''} ללשונית "פתוחים"?`);
    if (!ok) return;
    try {
      await axios.put(`${API}/admin/orders/${order.id}/status`, { status: 'processing' });
      // Roll the bonus calculation back — the delivery no longer counts.
      decrementBonusCounter();
      fetchOrders();
    } catch (e) {
      alert('שגיאה בהחזרה: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Revert: settled → completed. Clears the settled flag so the order
  // re-appears in "הושלמו" and the courier owes the cash again.
  const revertToCompleted = async (order) => {
    const ok = window.confirm(`להחזיר את ההזמנה של ${order.customer_name || ''} ללשונית "הושלמו"?\n(הסכום יחזור ליתרה לתשלום)`);
    if (!ok) return;
    try {
      await axios.post(`${API}/team/unsettle`, { order_ids: [order.id] });
      fetchOrders();
    } catch (e) {
      alert('שגיאה בהחזרה: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Coordination toggle - flips between "not coordinated yet" (orange) and
  // "coordinated with customer" (green). Visible to admin in the dashboard.
  // Optimistic update so the team member sees the change instantly even on
  // a slow connection.
  const toggleCoordinated = async (order) => {
    const next = !order.coordinated;
    setOrders((prev) =>
      prev.map((o) => (o.id === order.id ? { ...o, coordinated: next, coordinated_by: next ? staff?.name : null } : o))
    );
    try {
      await axios.patch(`${API}/admin/orders/${order.id}/field`, {
        coordinated: next,
        coordinated_by: next ? staff?.name : '',
        coordinated_at: next ? new Date().toISOString() : '',
      });
      // Re-sync after a moment to be sure (in case admin edited concurrently)
      setTimeout(fetchOrders, 800);
    } catch (e) {
      // Revert on error
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, coordinated: order.coordinated } : o))
      );
      alert('שגיאה בעדכון תיאום: ' + (e.response?.data?.detail || e.message));
    }
  };

  const deleteOrder = async (order) => {
    try {
      await axios.delete(`${API}/admin/orders/${order.id}`);
      fetchOrders();
    } catch (e) {
      alert('שגיאה במחיקה: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Cancel order with a reason (instead of hard-deleting). The order
  // stays in DB but moves to the "בוטלו" tab so the courier + admin can
  // still see why a particular delivery never happened.
  const cancelOrder = async (order, reason) => {
    try {
      await axios.put(`${API}/admin/orders/${order.id}/cancel`, { reason });
      fetchOrders();
    } catch (e) {
      alert('שגיאה בביטול: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ----- Stock calculation (must be before early return to keep hook order stable) -----
  // Refill orders = orders where the *customer* is the courier himself - that
  // means the boss sent him stock to deliver from. We sum the real `items[].quantity`
  // so a single refill of 23 units registers correctly (was previously hard-coded
  // to STOCK_PACK_SIZE per order, which silently capped large refills).
  const staffPhoneNorm = normalizePhone(staff?.phone || '');
  const refillOrders = allOrders.filter((o) => normalizePhone(o.customer_phone || '') === staffPhoneNorm);
  const completedCount = orders.filter((o) => o.status === 'completed').length;
  const refillQty = refillOrders.reduce((sum, o) => {
    const items = Array.isArray(o.items) ? o.items : [];
    const itemQty = items.reduce((s, it) => s + (parseInt(it.quantity, 10) || 0), 0);
    return sum + (itemQty || STOCK_PACK_SIZE); // legacy refill orders without items default to 6
  }, 0);

  // Manual stock overrides set by Bari (May 2026). When present, they
  // REPLACE the refill-orders math:
  //   stockRemaining = base_stock − (orders completed AFTER base_set_at)
  // Reset every time Bari saves a new value → counter restarts. We fetch
  // overrides once on mount and refresh every 60s + after any save.
  const [stockOverrides, setStockOverrides] = useState([]);
  const [productStock, setProductStock] = useState([]);
  const fetchOverrides = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/team/stock-overrides`),
        axios.get(`${API}/team/product-stock`),
      ]);
      setStockOverrides(Array.isArray(r1.data) ? r1.data : []);
      setProductStock(Array.isArray(r2.data) ? r2.data : []);
    } catch (_e) { /* graceful */ }
  }, []);
  useEffect(() => {
    fetchOverrides();
    const t = setInterval(fetchOverrides, 60000);
    return () => clearInterval(t);
  }, [fetchOverrides]);

  // Product catalog — used to show the ordered product (name + image) on
  // each order card, straight from the main system (Oct 2026 user spec).
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setCatalog(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const ownOverride = stockOverrides.find(o => o.courier_name === staff?.name);
  let stockBalance;
  let stockTotal;
  if (ownOverride) {
    // The override is the courier's REFILL count (e.g. Bari handed Yaniv
    // 30 packages). We then subtract every order Yaniv completed AFTER
    // that refill timestamp to show the live "remaining" number.
    // Fixed May 2026 per user request: "העלתי לו ל30 והוא מכר — תבדוק".
    const baseAt = new Date(ownOverride.base_set_at || 0).getTime();
    const completedSince = orders.filter(o => {
      if (o.status !== 'completed') return false;
      const tsStr = o.completed_at || o.updated_at || o.settled_at || o.created_at || '';
      const ts = tsStr ? new Date(tsStr).getTime() : 0;
      return ts >= baseAt;
    }).length;
    stockBalance = ownOverride.base_stock - completedSince;
    stockTotal = Math.max(ownOverride.base_stock, STOCK_PACK_SIZE);
  } else {
    stockBalance = refillQty - completedCount;
    stockTotal = Math.max(refillQty, STOCK_PACK_SIZE);
  }
  const stockRemaining = Math.max(0, stockBalance);
  // Low-stock alerts (blink + popup + notification) disabled per user request (Oct 2026).
  const lowStock = false;

  // Per-product remaining: base_stock − qty of matching items in orders the
  // courier completed since the base was set.
  const productStockRows = STOCK_PRODUCTS.map((p) => {
    const o = productStock.find((x) => x.courier_name === staff?.name && x.product_key === p.key);
    if (!o) return { ...p, remaining: null, total: 0 };
    const baseAt = new Date(o.base_set_at || 0).getTime();
    const used = orders.reduce((sum, ord) => {
      if (ord.status !== 'completed') return sum;
      const tsStr = ord.completed_at || ord.updated_at || ord.settled_at || ord.created_at || '';
      const ts = tsStr ? new Date(tsStr).getTime() : 0;
      if (ts < baseAt) return sum;
      const items = Array.isArray(ord.items) ? ord.items : [];
      return sum + items.reduce((s, it) => {
        const n = String(it.product_name || it.name || '').toLowerCase();
        return s + (p.match(n) ? (parseInt(it.quantity, 10) || 1) : 0);
      }, 0);
    }, 0);
    return { ...p, remaining: Math.max(0, o.base_stock - used), total: o.base_stock };
  });

  const lowStockNotifiedRef = useRef(false);
  const [lowStockPopup, setLowStockPopup] = useState(false);
  useEffect(() => {
    if (!staff) return;
    if (!lowStock) lowStockNotifiedRef.current = false;
  }, [lowStock, stockRemaining, staff, memberId]);

  if (!staff) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6" dir="rtl">
        <div className="text-center max-w-sm w-full">
          <img
            src="/products/logo.png"
            alt=""
            className="w-20 h-20 mx-auto mb-4"
          />
          <p className="text-xl font-bold text-stone-700 mb-6">בחר שליח</p>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => navigate('/team/bari')}
              className="block w-full bg-gradient-to-l from-amber-600 to-orange-600 text-white font-black py-5 rounded-2xl shadow-md text-lg"
              data-testid="pick-bari"
            >
              ברי
            </button>
            <button
              type="button"
              onClick={() => navigate('/team/liran')}
              className="block w-full bg-gradient-to-l from-teal-600 to-teal-700 text-white font-black py-5 rounded-2xl shadow-md text-lg"
              data-testid="pick-liran"
            >
              לירן
            </button>
            <button
              type="button"
              onClick={() => navigate('/team/yaniv')}
              className="block w-full bg-gradient-to-l from-indigo-600 to-indigo-700 text-white font-black py-5 rounded-2xl shadow-md text-lg"
              data-testid="pick-yaniv"
            >
              יניב
            </button>
            <button
              type="button"
              onClick={() => navigate('/team/daniel')}
              className="block w-full bg-gradient-to-l from-orange-600 to-rose-600 text-white font-black py-5 rounded-2xl shadow-md text-lg"
              data-testid="pick-daniel"
            >
              דניאל
            </button>
            {/* Quick-order entry — sits on the staff-picker page itself
                per user spec (June 2026). Opens a fresh /quick-order tab
                with a cache-busting timestamp so the form always loads
                in default state. */}
            <a
              href={`/quick-order?t=${Date.now()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gradient-to-l from-amber-400 to-orange-600 text-white font-black py-5 rounded-2xl shadow-lg text-lg mt-4 border-2 border-amber-300/50"
              data-testid="pick-quick-order"
            >
              ⚡ הזמנה מהירה
            </a>
            {/* Payment-link generator — admin picks products / sets a
                manual price → backend creates a CardCom LowProfile URL
                to copy and send. Sits under "הזמנה מהירה" per user spec. */}
            <a
              href={`/payment-link?t=${Date.now()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gradient-to-l from-cyan-500 to-blue-700 text-white font-black py-5 rounded-2xl shadow-lg text-lg border-2 border-cyan-300/50"
              data-testid="pick-payment-link"
            >
              🔗 לינק לתשלום
            </a>
            {/* Admin shortcut — jumps straight to the main /bari dashboard
                (orders, financials, WhatsApp CRM). Sits at the bottom of
                the picker so team members don't accidentally hit it. */}
            <a
              href="/bari"
              className="block w-full bg-stone-800 hover:bg-stone-900 text-white font-black py-4 rounded-2xl shadow-md text-base mt-2 border border-stone-700"
              data-testid="pick-main-dashboard"
            >
              🖥 דשבורד ראשי
            </a>
          </div>
        </div>
      </div>
    );
  }

  const openOrders = orders.filter((o) => o.status !== 'completed' && o.status !== 'cancelled');
  // "completed" tab = delivered & cash collected, but NOT yet settled with the supplier
  const completedOrders = orders.filter((o) => o.status === 'completed' && !o.settled);
  // "settled" tab = delivered AND courier already transferred the supplier-share
  const settledOrders = orders.filter((o) => o.status === 'completed' && o.settled);
  // "cancelled" tab = orders the courier marked as cancelled (with reason)
  const cancelledOrders = orders.filter((o) => o.status === 'cancelled');
  const list = view === 'open'
    ? openOrders
    : view === 'completed'
    ? completedOrders
    : view === 'settled'
    ? settledOrders
    : cancelledOrders;

  const bossPhoneIntl = `972${normalizePhone(BOSS_PHONE)}`;
  const stockMsg = `נגמר לי המלאי - נא לספק לי מלאי נוסף\nתודה מראש,\n${staff.name}`;
  const stockWaUrl = `whatsapp://send?phone=${bossPhoneIntl}&text=${encodeURIComponent(stockMsg)}`;

  // Payment summary (completed only) - ALL completed orders count as cash collected
  const cashCollected = completedOrders.reduce((s, o) => s + (o.total_amount || 0), 0);
  const shippingReward = completedOrders.length * SHIPPING_REWARD_PER_DELIVERY;
  const toSupplier = Math.max(0, cashCollected - shippingReward - bonusEarned);

  return (
    <div className="min-h-screen bg-stone-100 pb-24" dir="rtl">
      <header className={`bg-gradient-to-l ${staff.bg} text-white p-4 pb-6 rounded-b-3xl shadow-lg`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-white/70 text-[10px] font-medium">שליח</p>
            <h1 className="text-2xl font-black tracking-tight" data-testid="staff-name">
              {staff.name}
            </h1>
          </div>
          <img
            src="/products/logo.png"
            alt=""
            className="w-9 h-9 rounded-lg bg-white p-1"
          />
        </div>
        {/* Header stats - stock tile shown for Yaniv, Liran & Daniel
            (not Bari — per user request 2026-04-28: Bari delivers free,
            no stock allocation, the purple block was removed).
            Bonus tile: every 10 successful deliveries = 100₪ bonus.
            Shown next to the stock tile, NOT for Bari. */}
        {staff.name !== 'ברי' && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div
            className="backdrop-blur rounded-xl p-3 bg-white/15"
            data-testid="stock-display"
          >
            <div className="space-y-1">
              {productStockRows.map((p) => (
                <div key={p.key} className="flex items-center justify-between gap-2" data-testid={`stock-row-${p.key}`}>
                  <span className="text-[10px] font-bold text-white/90 truncate"><bdi>{p.label}</bdi></span>
                  <span className="text-base font-black leading-none">{p.remaining === null ? '—' : p.remaining}</span>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-white/80 mt-1.5 pt-1 border-t border-white/20 text-center">מלאי</div>
          </div>
          <div
            className="backdrop-blur rounded-xl p-3 text-center cursor-pointer bg-gradient-to-l from-yellow-400/30 to-amber-500/30 border border-yellow-300/50"
            onClick={() => setShowBonusWelcome(true)}
            data-testid="bonus-display"
          >
            <div className="text-2xl font-black flex items-center justify-center gap-1">
              <span>🎁</span>
              <span data-testid="bonus-progress">{bonusCounter}/10</span>
            </div>
            <div className="text-[10px] text-white/95 leading-tight">משלוחים לבונוס<br/><span className="font-bold text-yellow-200">100 ש"ח</span></div>
          </div>
        </div>
        )}
      </header>

      {/* Stock manager — visible ONLY in Bari's app (May 2026 per user
          request). Bari can manually set the current stock value for
          each of the other couriers (Yaniv / Liran / Daniel). Saving
          stamps `base_set_at` server-side; the courier's app then shows
          `base_stock − (orders completed after base_set_at)` so the
          value stays accurate live as he ships, until Bari refills him
          and saves a new value. */}
      {staff.name === 'ברי' && (
        <CourierStockManager overrides={stockOverrides} productStock={productStock} onSaved={fetchOverrides} />
      )}

      {/* Install banner - always show unless already installed */}
      {canInstall && !isStandalone() && (
        <button
          onClick={installApp}
          className="mx-4 mt-4 w-[calc(100%-2rem)] bg-[#D97706] hover:bg-[#b86205] text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2 shadow-md"
          data-testid="install-pwa-btn"
        >
          <Download className="w-5 h-5" />
          התקן את האפליקציה על המסך הבית
        </button>
      )}
      {!canInstall && !isStandalone() && (
        <button
          onClick={() => setShowHowToInstall(true)}
          className="mx-4 mt-4 w-[calc(100%-2rem)] bg-white border-2 border-[#D97706] text-[#D97706] rounded-xl py-3 font-bold flex items-center justify-center gap-2"
          data-testid="install-help-btn"
        >
          <Download className="w-5 h-5" />
          הוסף למסך הבית
        </button>
      )}
      {showHowToInstall && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowHowToInstall(false)} data-testid="install-modal">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-black mb-3 text-[#D97706]">איך להתקין?</h3>
            <div className="space-y-3 text-sm text-stone-700 leading-relaxed">
              <div>
                <p className="font-bold mb-1">📱 iPhone (Safari):</p>
                <p>1. לחץ על כפתור השיתוף <span className="inline-block border border-stone-300 rounded px-1.5 py-0.5 text-xs">⎙</span> למטה<br/>2. גלול למטה והקש על "הוסף למסך הבית"</p>
              </div>
              <div>
                <p className="font-bold mb-1">🤖 Android (Chrome):</p>
                <p>1. לחץ על ה-3 נקודות <span className="inline-block border border-stone-300 rounded px-1.5 py-0.5 text-xs">⋮</span> למעלה<br/>2. בחר "הוסף למסך הבית" / "Install app"</p>
              </div>
            </div>
            <button onClick={() => setShowHowToInstall(false)} className="mt-5 w-full bg-stone-100 hover:bg-stone-200 py-2.5 rounded-xl font-bold text-stone-700">הבנתי</button>
          </div>
        </div>
      )}
      {showIOSInstall && !isStandalone() && (
        <div className="mx-4 mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-900 leading-relaxed" data-testid="ios-install-hint">
          <div className="flex items-start gap-2">
            <Share2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold mb-1">התקן כאפליקציה ב-iPhone:</p>
              <p>לחץ על <span className="inline-block border border-blue-400 rounded px-1.5 py-0.5 font-bold">⎙ שתף</span> למטה → גלול למטה → "הוסף למסך הבית"</p>
            </div>
            <button onClick={() => setShowIOSInstall(false)} className="text-blue-700 font-bold text-xs">✕</button>
          </div>
        </div>
      )}

      {/* Payment summary - ALWAYS visible for all couriers (Bari/Yaniv/Liran)
          per user request: the three apps should look identical regardless
          of how many orders are in each tab. If there are no completed or
          settled orders yet, the 3 cubes still render (all zeros) so the
          layout matches Yaniv's app 1:1. */}
      {true && (
        <div className="mx-4 mt-4 bg-white rounded-2xl shadow-sm p-4" data-testid="payment-summary">
          <div className="flex items-center gap-2 mb-3">
            <Banknote className="w-5 h-5 text-green-600" />
            <h3 className="font-black text-stone-800">סיכום כספי</h3>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-green-50 border-2 border-green-400 rounded-xl p-3 text-center">
              <p className="text-[10px] text-stone-500 mb-1">סה״כ שנגבה</p>
              <p className="text-lg font-black text-green-600" data-testid="cash-collected-total">{cashCollected} ש״ח</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <p className="text-[10px] text-stone-500 mb-0.5">תשלום עבור משלוחים</p>
              <p className="text-base font-black text-blue-600" data-testid="shipping-reward">{shippingReward} ש״ח</p>
              <p className="text-[9px] text-stone-400">{completedOrders.length} × 40</p>
              <div className="border-t border-blue-200 my-1.5"></div>
              <p className="text-[10px] text-stone-500 mb-0.5">בונוס (כל 10 משלוחים)</p>
              <p className="text-base font-black text-emerald-600" data-testid="bonus-earned">{bonusEarned} ש״ח</p>
              <div className="border-t border-blue-200 my-1.5"></div>
              <p className="text-[10px] text-stone-500 mb-0.5">סה״כ</p>
              <p className="text-lg font-black text-blue-800" data-testid="courier-total-pay">{shippingReward + bonusEarned} ש״ח</p>
            </div>
            <button
              type="button"
              onClick={() => completedOrders.length > 0 && setShowShipmentPicker(true)}
              disabled={completedOrders.length === 0}
              className={`rounded-xl p-3 text-center shadow-sm transition-all ${
                completedOrders.length > 0
                  ? 'bg-gradient-to-b from-amber-50 to-amber-100 border-2 border-amber-400 hover:border-amber-500 hover:from-amber-100 hover:to-amber-200 active:scale-[0.97] cursor-pointer'
                  : 'bg-stone-50 border-2 border-stone-200 cursor-not-allowed opacity-70'
              }`}
              data-testid="open-settle-modal"
            >
              <p className="text-[10px] text-stone-600 mb-1">סה״כ לבריאות הגבר</p>
              <p className={`text-lg font-black ${completedOrders.length > 0 ? 'text-amber-800' : 'text-stone-500'}`} data-testid="to-supplier-total">{toSupplier} ש״ח</p>
              {completedOrders.length > 0 ? (
                <p className="text-[9px] text-amber-700 font-bold mt-0.5 flex items-center justify-center gap-1">
                  <CreditCard className="w-3 h-3" />
                  מעבר לתשלום
                </p>
              ) : (
                <p className="text-[9px] text-stone-400 mt-0.5">אין יתרה לתשלום</p>
              )}
            </button>
          </div>
          {/* "תשלומים ששולמו עד כה" card removed (May 2026) per user
              request — was showing inflated cumulative numbers ("6208 ש״ח
              · 4 סגירות") that didn't match the team's actual behaviour
              and confused all three couriers. The dedicated "שולמו" tab
              below shows the same data correctly per-period. */}
        </div>
      )}

      {/* Tabs */}
      <div className="px-4 mt-4 flex gap-2">
        <button
          onClick={() => setView('open')}
          data-testid="tab-open"
          className={`flex-1 py-3 rounded-2xl font-bold text-xs transition-all ${view === 'open' ? 'text-white shadow-md' : 'bg-white text-stone-500'}`}
          style={view === 'open' ? { backgroundColor: staff.color } : {}}
        >
          <Clock className="w-4 h-4 inline ml-1" />
          פתוחים ({openOrders.length})
        </button>
        <button
          onClick={() => setView('completed')}
          data-testid="tab-completed"
          className={`flex-1 py-3 rounded-2xl font-bold text-xs transition-all ${view === 'completed' ? 'bg-green-600 text-white shadow-md' : 'bg-white text-stone-500'}`}
        >
          <CheckCircle className="w-4 h-4 inline ml-1" />
          הושלמו ({completedOrders.length})
        </button>
        <button
          onClick={() => setView('settled')}
          data-testid="tab-settled"
          className={`flex-1 py-3 rounded-2xl font-bold text-xs transition-all ${view === 'settled' ? 'bg-emerald-700 text-white shadow-md' : 'bg-white text-stone-500'}`}
        >
          <Wallet className="w-4 h-4 inline ml-1" />
          שולמו ({settledOrders.length})
        </button>
        <button
          onClick={() => setView('cancelled')}
          data-testid="tab-cancelled"
          className={`flex-1 py-3 rounded-2xl font-bold text-xs transition-all ${view === 'cancelled' ? 'bg-red-600 text-white shadow-md' : 'bg-white text-stone-500'}`}
        >
          <Trash2 className="w-4 h-4 inline ml-1" />
          בוטלו ({cancelledOrders.length})
        </button>
      </div>

      <div className="px-4 mt-4 space-y-4">
        {loading ? (
          <div className="text-center py-12 text-stone-400">טוען...</div>
        ) : list.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 font-medium">
              {view === 'open' && 'אין משלוחים פתוחים כרגע'}
              {view === 'completed' && 'עוד לא נמסרו משלוחים שמחכים להעברה'}
              {view === 'settled' && 'עוד לא נסגרו משלוחים סופית'}
              {view === 'cancelled' && 'אין הזמנות שבוטלו'}
            </p>
          </div>
        ) : (
          list.map((o) => <OrderCard key={o.id} order={o} catalog={catalog} onComplete={() => markCompleted(o)} onDelete={() => deleteOrder(o)} onCancel={(reason) => cancelOrder(o, reason)} onToggleCoordinate={() => toggleCoordinated(o)} onRevertOpen={() => revertToOpen(o)} onRevertCompleted={() => revertToCompleted(o)} onUpdateOrder={(patch) => setOrders((prev) => prev.map(x => x.id === o.id ? { ...x, ...patch } : x))} isCompleted={view === 'completed' || view === 'settled'} isCancelled={view === 'cancelled'} staffName={staff.name} />)
        )}
      </div>

      {/* Success popup */}
      {successPopup && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="success-popup">
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center max-w-xs w-full animate-in zoom-in duration-300">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <CheckCircle className="w-12 h-12 text-white" strokeWidth={3} />
            </div>
            <h3 className="text-2xl font-black text-stone-800 mb-2">התשלום בוצע בהצלחה</h3>
            <p className="text-stone-500 text-sm">ההזמנה עברה ל״הושלמו״</p>
          </div>
        </div>
      )}


      {/* Bonus celebration popup — fires when the courier hits 10 successful
          deliveries. Auto-closes after 6s OR on tap. */}
      {showBonusPopup && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4 animate-fade-in" onClick={() => setShowBonusPopup(false)} data-testid="bonus-popup">
          <div className="bg-gradient-to-br from-yellow-300 via-amber-400 to-orange-500 rounded-3xl shadow-2xl max-w-sm w-full p-6 text-center text-white relative overflow-hidden">
            <div className="text-6xl mb-3 animate-bounce">🎉</div>
            <h2 className="text-2xl font-black mb-2">ברכותי - עשית זאת!</h2>
            <p className="text-base font-bold mb-1">10 משלוחים בהצלחה 🚚</p>
            <p className="text-3xl font-black my-3 bg-white/20 rounded-xl py-2">קיבלת בונוס של 100 ש"ח</p>
            <p className="text-sm font-bold opacity-90 mb-4">תודה על העשייה :)</p>
            <button
              onClick={() => setShowBonusPopup(false)}
              className="bg-white text-amber-700 rounded-xl px-6 py-2.5 font-black text-sm shadow-md hover:scale-105 transition-transform"
              data-testid="bonus-close"
            >
              המשך לעבוד 💪
            </button>
          </div>
        </div>
      )}

      {/* Bonus welcome popup removed (Jun 2026) per user request */}
      {/* Settlement modal - "מעבר לתשלום".
          Receives a subset of orders (`settleTargetOrders`) picked via
          `ShipmentPickerModal`. Falls back to the full completed list for
          backwards compat (never happens via the UI today). */}
      {showSettleModal && (() => {
        const target = settleTargetOrders.length > 0 ? settleTargetOrders : completedOrders;
        const tCash = target.reduce((s, o) => s + (o.total_amount || 0), 0);
        const tReward = target.length * SHIPPING_REWARD_PER_DELIVERY;
        // The accumulated bonus is deducted from the transfer — the courier
        // keeps it from the cash he collected. Consumed on settlement done.
        const tSupplier = Math.max(0, tCash - tReward - bonusEarned);
        return (
          <SettlementModal
            onClose={() => setShowSettleModal(false)}
            onDone={() => { setShowSettleModal(false); setSettleTargetOrders([]); consumeBonus(); fetchOrders(); }}
            orders={target}
            cashCollected={tCash}
            shippingReward={tReward}
            toSupplier={tSupplier}
            paymentConfig={paymentConfig}
            staffName={staff.name}
          />
        );
      })()}
      {/* Shipment-picker modal - "בחר איזה משלוח לשלם" */}
      {showShipmentPicker && (
        <ShipmentPickerModal
          orders={completedOrders}
          onClose={() => setShowShipmentPicker(false)}
          onPick={(picked) => {
            setSettleTargetOrders(picked);
            setShowShipmentPicker(false);
            setShowSettleModal(true);
          }}
        />
      )}
      {/* Payment-history ledger - past settlements + "pay more" CTA */}
      {showPaymentHistory && (
        <PaymentHistoryModal
          onClose={() => setShowPaymentHistory(false)}
          settledOrders={settledOrders}
          completedOrders={completedOrders}
          onPayMore={() => setShowSettleModal(true)}
          onAfterUndo={() => fetchOrders()}
        />
      )}
    </div>
  );
};

const OrderCard = ({ order, catalog, onComplete, onDelete, onCancel, onToggleCoordinate, onRevertOpen, onRevertCompleted, onUpdateOrder, isCompleted, isCancelled, staffName }) => {
  // Cancel-with-reason modal: when the courier taps the new ✕ button on
  // an open card, this modal asks for a brief reason ("הלקוח סירב",
  // "כתובת שגויה", etc). The order then moves to the "בוטלו" tab and
  // the reason is stored on the order (visible to admin too).
  const [showCancelModal, setShowCancelModal] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState('');
  const COMMON_REASONS = ['הלקוח סירב', 'כתובת שגויה', 'הלקוח לא ענה', 'הלקוח ביטל', 'אחר'];
  const submitCancel = () => {
    const reason = (cancelReason || '').trim();
    if (!reason) { alert('אנא בחר או הזן סיבה'); return; }
    onCancel && onCancel(reason);
    setShowCancelModal(false);
    setCancelReason('');
  };
  // Format the order creation date so the courier can see how old it is
  // at a glance ("6 ביוני, 14:32"). Falls back to "—" if missing.
  const createdAtStr = (() => {
    const raw = order.created_at || order.createdAt;
    if (!raw) return '';
    try {
      const d = new Date(raw);
      return d.toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  })();
  // Note: delete confirmation now uses a native window.confirm popup
  // instead of a 2-click "armed" state - clearer UX, no chance of accidental
  // taps timing out. State variable removed.
  // Editable notes panel — courier can add/edit a note per order on the
  // go (e.g. "buzz the third buzzer", "left at neighbor"). Persists via
  // PATCH /admin/orders/{id}/field. Added May 2026 per user request.
  const [editingNotes, setEditingNotes] = React.useState(false);
  const [draftNotes, setDraftNotes] = React.useState(order.notes || '');
  const [savingNotes, setSavingNotes] = React.useState(false);
  React.useEffect(() => { setDraftNotes(order.notes || ''); }, [order.notes]);
  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      // /admin/orders/{id}/field accepts a key/value patch object directly.
      await axios.patch(`${API}/admin/orders/${order.id}/field`, { notes: draftNotes });
      if (onUpdateOrder) onUpdateOrder({ notes: draftNotes });
      setEditingNotes(false);
    } catch (_e) { /* keep editing if it failed */ }
    setSavingNotes(false);
  };
  const phone = (order.customer_phone || '').replace(/[-\s]/g, '');
  const phoneIntl = `972${phone.replace(/^0/, '')}`;
  // Direct-dial link — opens the native dialer (iOS/Android) so the
  // courier can call the customer with one tap.
  const telUrl = `tel:${phone}`;
  // Pre-filled WhatsApp message — user-supplied template (May 2026).
  // Format: "שלום [שם הלקוח] / עשית הזמנה על סך: [סכום] / המשלוח שלך
  // אצלי, אתאם איתך בקרוב את מסירת המשלוח / תודה ויום מקסים / תודה
  // ויום טוב". The customer's name (NOT the courier) opens the message
  // — this is the "from courier to customer" greeting.
  const customerName = (order.customer_name || '').trim();
  const amount = order.total_amount || 0;
  const waMsg = `שלום ${customerName}\nעשית הזמנה על סך: ${amount} ש״ח\n\nהמשלוח שלך אצלי, אתאם איתך בקרוב את מסירת המשלוח\n\nתודה ויום מקסים`;
  const waUrl = `whatsapp://send?phone=${phoneIntl}&text=${encodeURIComponent(waMsg)}`;
  const addr = order.customer_address || '';
  const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(addr)}&navigate=yes`;

  return (
    <div
      className={`bg-white rounded-2xl shadow-md overflow-hidden ${isCompleted ? 'border-4 border-green-400' : (order.coordinated ? 'border-4 border-green-500' : 'border-4 border-amber-400')}`}
      data-testid={`order-${order.id}`}
    >
      {/* Created-at row — shown above the payment banner so the courier
          can see at a glance how old the order is. Cancelled orders also
          show their reason here in red. */}
      {(createdAtStr || (isCancelled && order.cancel_reason)) && (
        <div className="px-4 pt-3 pb-1 flex items-center justify-between gap-2" data-testid={`order-meta-${order.id}`}>
          {createdAtStr && (
            <div className="text-[11px] text-stone-500 font-medium inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span dir="ltr">{createdAtStr}</span>
            </div>
          )}
          {isCancelled && order.cancel_reason && (
            <div className="text-[11px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-0.5 truncate max-w-[60%]" data-testid={`cancel-reason-${order.id}`} title={order.cancel_reason}>
              ✕ {order.cancel_reason}
            </div>
          )}
        </div>
      )}

      {/* Payment banner — 3 states:
           1. isCompleted   → "שולם / נמסר" green banner
           2. prepaid (cc/bit/bank/paypal/paybox) → "שולם מראש - אין צורך לגבות" green
           3. cash (default) → orange "הסכום שיש לשלם" */}
      {(() => {
        const PREPAID = ['credit_card','bit','bank_transfer','paypal','paybox'];
        const isPrepaid = PREPAID.includes(order.payment_method);
        if (isCompleted) {
          return (
            <div className="p-4 bg-green-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500 mb-0.5">{isPrepaid ? 'שולם מראש' : 'שולם'}</p>
                  <p className="text-3xl font-black text-green-600">{order.total_amount} ש״ח</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
            </div>
          );
        }
        if (isCancelled) {
          return (
            <div className="p-4 bg-red-50">
              <div className="text-center">
                <p className="text-[13px] text-red-700 mb-1 font-bold">הזמנה בוטלה</p>
                <p className="text-3xl font-black text-red-900">{order.total_amount} ש״ח</p>
              </div>
            </div>
          );
        }
        if (isPrepaid) {
          // Prepaid orders (May 2026 user spec) — green banner with
          // "שולם מראש - אין צורך לגבות" so the courier doesn't bother
          // the customer about money.
          return (
            <div className="p-4 bg-emerald-100 border-2 border-emerald-300" data-testid={`prepaid-banner-${order.id}`}>
              <div className="text-center">
                <div className="inline-flex items-center gap-2 mb-1">
                  <CheckCircle className="w-5 h-5 text-emerald-700" />
                  <p className="text-[13px] text-emerald-800 font-bold">שולם מראש - אין צורך לגבות</p>
                </div>
                <p className="text-3xl font-black text-emerald-700 line-through opacity-60">
                  {order.total_amount} ש״ח
                </p>
                <p className="text-[11px] text-emerald-700 mt-1 font-bold">
                  {order.payment_method === 'credit_card' && '💳 כרטיס אשראי'}
                  {order.payment_method === 'bit' && '🔵 Bit'}
                  {order.payment_method === 'bank_transfer' && '🏦 העברה בנקאית'}
                  {order.payment_method === 'paypal' && 'PayPal'}
                  {order.payment_method === 'paybox' && 'PayBox'}
                </p>
              </div>
            </div>
          );
        }
        return (
          <div className="p-4 bg-amber-100">
            <div className="text-center">
              <p className="text-[13px] text-amber-800 mb-1 font-bold">הסכום שיש לשלם</p>
              <p className="text-4xl font-black text-amber-900" data-testid={`amount-${order.id}`}>
                {order.total_amount} ש״ח
              </p>
            </div>
          </div>
        );
      })()}

      {/* Ordered products — name + image from the main system, shown big
          right under the amount (Oct 2026 user spec). */}
      {!isCancelled && Array.isArray(order.items) && order.items.length > 0 && (
        <div className="px-4 pt-3 space-y-2" data-testid={`order-products-${order.id}`}>
          {order.items.map((it, i) => {
            const prod = (catalog || []).find((p) => p.id === it.product_id)
              || (catalog || []).find((p) => (p.name || '') === (it.product_name || ''));
            const img = prod?.image || prod?.imageUrl;
            const qty = parseInt(it.quantity, 10) || 1;
            return (
              <div key={i} className="flex items-center gap-3 bg-stone-50 border-2 border-stone-200 rounded-xl p-2.5" data-testid={`order-product-${order.id}-${i}`}>
                {img && (
                  <img src={img} alt="" className="w-16 h-16 rounded-lg object-contain bg-white border border-stone-200 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-black text-stone-800 leading-tight"><bdi>{it.product_name || it.name}</bdi></p>
                  {qty > 1 && <p className="text-sm font-bold text-amber-700 mt-0.5">× {qty} יחידות</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="p-4 space-y-2">
        <div>
          <p className="text-xs text-stone-400 mb-0.5">שם הלקוח</p>
          <p className="text-lg font-bold text-stone-800">{order.customer_name}</p>
        </div>
        <div>
          <p className="text-xs text-stone-400 mb-0.5">טלפון</p>
          <a
            href={telUrl}
            className="inline-flex items-center gap-2 bg-blue-50 active:bg-blue-100 border border-blue-200 px-3 py-2 rounded-xl font-mono font-bold text-blue-700 text-lg select-none"
            data-testid={`call-${order.id}`}
            dir="ltr"
          >
            <Phone className="w-5 h-5 text-blue-600" />
            {order.customer_phone}
          </a>
        </div>
        <div>
          <p className="text-xs text-stone-400 mb-0.5">כתובת</p>
          <p className="text-base font-medium text-stone-700 leading-snug">{addr || '-'}</p>
        </div>
        {/* Notes — editable rubric. Always visible so the courier can add
            a note even if none exists yet. */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2" data-testid={`notes-rubric-${order.id}`}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-amber-800 font-bold flex items-center gap-1">
              <FileText className="w-3 h-3" />
              הערות
            </p>
            {!editingNotes && (
              <button
                type="button"
                onClick={() => setEditingNotes(true)}
                className="text-[11px] text-amber-700 font-bold underline"
                data-testid={`notes-edit-${order.id}`}
              >
                {order.notes ? 'ערוך' : 'הוסף'}
              </button>
            )}
          </div>
          {editingNotes ? (
            <>
              <textarea
                value={draftNotes}
                onChange={(e) => setDraftNotes(e.target.value)}
                placeholder="כתוב הערה..."
                rows={2}
                className="w-full bg-white border border-amber-300 focus:border-amber-500 outline-none rounded-lg p-2 text-sm text-stone-800"
                data-testid={`notes-input-${order.id}`}
                autoFocus
              />
              <div className="flex gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={saveNotes}
                  disabled={savingNotes}
                  className="flex-1 bg-amber-600 active:bg-amber-700 disabled:opacity-60 text-white rounded-lg py-1.5 text-xs font-black"
                  data-testid={`notes-save-${order.id}`}
                >
                  {savingNotes ? 'שומר...' : 'שמור'}
                </button>
                <button
                  type="button"
                  onClick={() => { setDraftNotes(order.notes || ''); setEditingNotes(false); }}
                  disabled={savingNotes}
                  className="px-3 bg-stone-200 active:bg-stone-300 text-stone-700 rounded-lg py-1.5 text-xs font-black"
                  data-testid={`notes-cancel-${order.id}`}
                >
                  בטל
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-amber-900 min-h-[20px]" data-testid={`notes-text-${order.id}`}>
              {order.notes || <span className="text-amber-400 italic">אין הערות עדיין</span>}
            </p>
          )}
        </div>
      </div>

      {!isCompleted && (
        <div className="p-3 pt-0 space-y-2">
          {/* Coordination toggle - orange until clicked, then bright green */}
          <button
            onClick={onToggleCoordinate}
            className={`w-full rounded-xl py-3 font-black text-base shadow-md transition-all flex items-center justify-center gap-2 ${
              order.coordinated
                ? 'bg-green-500 active:bg-green-600 text-white'
                : 'bg-orange-500 active:bg-orange-600 text-white'
            }`}
            data-testid={`coordinate-${order.id}`}
          >
            {order.coordinated ? (
              <>
                <CheckCircle className="w-5 h-5" />
                קבעתי עם הלקוח
              </>
            ) : (
              <>
                <Clock className="w-5 h-5" />
                טרם קבעתי עם הלקוח
              </>
            )}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <a
              href={waUrl}
              className="flex items-center justify-center gap-2 bg-green-500 active:bg-green-600 text-white rounded-xl py-3 font-bold text-sm"
              data-testid={`wa-${order.id}`}
            >
              <MessageCircle className="w-5 h-5" />
              וואטסאפ
            </a>
            <a
              href={wazeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-blue-500 active:bg-blue-600 text-white rounded-xl py-3 font-bold text-sm"
              data-testid={`waze-${order.id}`}
            >
              <Navigation className="w-5 h-5" />
              וויז
            </a>
          </div>
          <button
            onClick={onComplete}
            className="w-full bg-orange-500 active:bg-orange-600 text-white rounded-xl py-4 font-black text-base shadow-md flex items-center justify-center gap-2"
            data-testid={`complete-${order.id}`}
          >
            <Clock className="w-5 h-5" />
            טרם נמסר - לחץ אחרי מסירה
          </button>
          {/* Cancel-with-reason button. Opens a modal asking why the
              order was cancelled — keeps the order in DB but moves it to
              the "בוטלו" tab so it stays auditable. */}
          <button
            onClick={() => setShowCancelModal(true)}
            className="w-full bg-red-100 active:bg-red-200 text-red-700 border-2 border-red-300 rounded-xl py-2.5 font-bold text-sm flex items-center justify-center gap-2"
            data-testid={`cancel-${order.id}`}
          >
            <Trash2 className="w-4 h-4" />
            בטל הזמנה
          </button>
        </div>
      )}

      {isCompleted && (
        <div className="p-3 pt-0 space-y-2">
          <div className="flex items-center gap-2">
            {order.settled ? (
              <div className="flex-1 bg-emerald-700 text-white rounded-xl py-3 font-black text-sm text-center flex flex-col items-center justify-center">
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  שולם לבריאות הגבר
                </div>
                {order.settled_method && (
                  <div className="text-[11px] font-medium text-white/85 mt-0.5">
                    {METHOD_LABELS[order.settled_method] || order.settled_method}
                    {/* Show this specific order's supplier share (total - 40₪
                        delivery bonus), not the whole-batch amount that's
                        stored in `settled_amount`. That way each settled card
                        shows its own contribution to the settlement. */}
                    {order.total_amount ? ` · ${Math.max(0, (order.total_amount || 0) - SHIPPING_REWARD_PER_DELIVERY)} ש״ח` : ''}
                  </div>
                )}
                {/* Settlement timestamp — exact date + time of when the
                    courier transferred the cash, so the admin can match
                    settlements to the bank/cash log. */}
                {order.settled_at && (
                  <div className="text-[10px] font-medium text-white/70 mt-0.5" dir="ltr" data-testid={`settled-at-${order.id}`}>
                    {new Date(order.settled_at).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 bg-green-500 text-white rounded-xl py-3 font-black text-base text-center flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5" />
                נמסר ✓
              </div>
            )}
            <button
              onClick={() => {
                const ok = window.confirm(`האם למחוק את ההזמנה של ${order.customer_name || ''} (${order.total_amount || 0} ש״ח)?\n\nפעולה זו אינה ניתנת לביטול.`);
                if (ok) onDelete();
              }}
              className="flex items-center justify-center rounded-xl w-14 h-[48px] bg-red-100 text-red-600 hover:bg-red-200 active:bg-red-300 transition-all"
              title="מחק הזמנה"
              data-testid={`delete-completed-${order.id}`}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
          {/* Revert button — sends order back one tab in case the courier
              tapped "נמסר" / "שולם" by accident. */}
          {!order.settled && onRevertOpen && (
            <button
              onClick={onRevertOpen}
              className="w-full flex items-center justify-center gap-2 rounded-xl py-2.5 bg-amber-50 border border-amber-300 text-amber-800 font-bold text-xs active:bg-amber-100 transition-all"
              data-testid={`revert-open-${order.id}`}
            >
              <ArrowLeft className="w-4 h-4 rotate-180" />
              החזר לפתוחים
            </button>
          )}
          {order.settled && onRevertCompleted && (
            <button
              onClick={onRevertCompleted}
              className="w-full flex items-center justify-center gap-2 rounded-xl py-2.5 bg-amber-50 border border-amber-300 text-amber-800 font-bold text-xs active:bg-amber-100 transition-all"
              data-testid={`revert-completed-${order.id}`}
            >
              <ArrowLeft className="w-4 h-4 rotate-180" />
              החזר להושלמו
            </button>
          )}
        </div>
      )}

      {/* Cancel-with-reason modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowCancelModal(false)} data-testid={`cancel-modal-${order.id}`}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-black text-lg text-stone-800 mb-1">ביטול הזמנה</h3>
            <p className="text-xs text-stone-500 mb-4">בחר סיבה או הזן בעצמך</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {COMMON_REASONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setCancelReason(r)}
                  className={`px-3 py-2 rounded-lg text-xs font-bold border-2 transition-colors ${cancelReason === r ? 'bg-red-600 text-white border-red-600' : 'bg-white text-stone-700 border-stone-300'}`}
                  data-testid={`cancel-reason-preset-${r}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="או הזן סיבה אחרת..."
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid={`cancel-reason-input-${order.id}`}
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setShowCancelModal(false); setCancelReason(''); }}
                className="flex-1 px-3 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg font-bold text-stone-700 text-sm"
              >
                ביטול
              </button>
              <button
                onClick={submitCancel}
                disabled={!cancelReason.trim()}
                className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold text-sm disabled:opacity-50"
                data-testid={`cancel-confirm-${order.id}`}
              >
                בטל הזמנה
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// METHOD_LABELS is referenced by both OrderCard (settled badge) and the
// payment-history button, which is rendered inside TeamApp. Hoisting the
// const above the components that use it keeps the closure references
// resolved without forcing a useMemo.
const METHOD_LABELS = {
  bit: 'ביט',
  paypal: 'PayPal',
  paybox: 'פייבוקס',
  bank: 'העברה בנקאית',
  cash: 'מזומן',
};

export default TeamApp;

// ---------------------------------------------------------------------------
// Settlement modal - lets the courier pick how he sent the supplier-share
// ("toSupplier") to the business owner, and fires a pre-filled WhatsApp
// confirmation message to the boss number once done.
//
// UX flow:
//  1. Pick method (BIT / PayPal / PayBox / Bank / Cash)
//  2. Per-method screen shows the amount + recipient details (each with a
//     "copy" button), a deep-link "open app" button, and a confirm button
//     that posts /api/team/settle and opens a pre-filled WhatsApp message.
//  3. Bank transfer renders a dedicated 4-field card (bank/account/branch/
//     name) matching the existing customer-facing bank-transfer flow.
//  4. Cash is intentionally barebones - it's only used when the courier
//     hands the money in person to whoever brings him new stock.
// ---------------------------------------------------------------------------

const METHOD_DEFS = [
  { id: 'bit',    label: 'ביט',           hebLabel: 'ביט',           color: 'from-cyan-500 to-blue-500',      icon: Wallet },
  { id: 'paypal', label: 'PayPal',        hebLabel: 'PayPal',        color: 'from-blue-600 to-indigo-700',    icon: CreditCard },
  { id: 'paybox', label: 'פייבוקס',       hebLabel: 'פייבוקס',       color: 'from-purple-500 to-pink-500',    icon: Wallet },
  { id: 'bank',   label: 'העברה בנקאית',  hebLabel: 'העברה בנקאית',  color: 'from-stone-600 to-stone-800',    icon: Building2 },
  { id: 'cash',   label: 'מזומן',         hebLabel: 'מזומן',         color: 'from-emerald-500 to-emerald-700', icon: Coins },
];

const normalizePhone2 = (p) => (p || '').replace(/[-\s]/g, '').replace(/^972/, '').replace(/^0/, '');

// Tiny self-contained "copy to clipboard" pill button, with green check-mark
// flash for 1.5s after a successful copy.
const CopyPill = ({ value, label, testid }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value || ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      // Fallback for old browsers / WebView
      const ta = document.createElement('textarea');
      ta.value = String(value || '');
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_e2) {}
      document.body.removeChild(ta);
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${copied ? 'bg-green-100 text-green-700' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'}`}
      data-testid={testid || 'copy-pill'}
      aria-label={label || 'העתק'}
    >
      {copied ? '✓ הועתק' : '📋 העתק'}
    </button>
  );
};
// ShipmentPickerModal — "בחר איזה משלוח לשלם".
// Opened from the amber "סה״כ לבריאות הגבר" tile. Lets the courier
// settle shipments either one-by-one or all at once. Per shipment:
// shows the customer name, total, and a per-row "שלם" button that
// closes the picker and launches the SettlementModal pre-scoped to
// just that order. The big green "שלם את הכל" button at the bottom
// does the same for every unsettled delivered order. The per-shipment
// flow allows the courier to split the settlement across multiple
// transfers if they can't cover the full amount at once.
const ShipmentPickerModal = ({ orders, onClose, onPick }) => {
  const totalCash = orders.reduce((s, o) => s + (o.total_amount || 0), 0);
  const totalSupplier = Math.max(0, totalCash - orders.length * SHIPPING_REWARD_PER_DELIVERY);
  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="shipment-picker-modal"
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-l from-amber-600 to-amber-500 text-white p-5 rounded-t-3xl flex items-center justify-between z-10">
          <div>
            <p className="text-white/80 text-xs font-medium">בחר איזה משלוח לשלם</p>
            <h2 className="text-xl font-black">{orders.length} משלוחים ממתינים</h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center font-bold"
            data-testid="close-shipment-picker"
          >
            ✕
          </button>
        </div>

        {/* Scrollable shipment list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {orders.map((o) => {
            const share = Math.max(0, (o.total_amount || 0) - SHIPPING_REWARD_PER_DELIVERY);
            return (
              <div
                key={o.id}
                className="bg-stone-50 border border-stone-200 rounded-xl p-3 flex items-center justify-between gap-3"
                data-testid={`picker-row-${o.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-stone-800 truncate" data-testid={`picker-name-${o.id}`}>
                    {o.customer_name || 'ללא שם'}
                  </p>
                  <p className="text-[11px] text-stone-500">
                    נגבה: <strong className="text-green-700">{o.total_amount} ש״ח</strong>
                    <span className="mx-1">·</span>
                    לבריאות הגבר: <strong className="text-amber-700">{share} ש״ח</strong>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onPick([o])}
                  className="bg-amber-500 hover:bg-amber-600 active:scale-[0.97] text-white font-black text-sm rounded-lg px-4 py-2 shadow transition-all flex-shrink-0"
                  data-testid={`picker-pay-${o.id}`}
                >
                  שלם {share} ש״ח
                </button>
              </div>
            );
          })}
        </div>

        {/* Sticky footer: "שלם הכל" */}
        <div className="border-t border-stone-200 p-4 bg-white">
          <button
            type="button"
            onClick={() => onPick(orders)}
            className="w-full bg-green-600 hover:bg-green-700 active:scale-[0.99] text-white rounded-xl py-4 font-black text-base flex items-center justify-center gap-2 shadow-md transition-all"
            data-testid="picker-pay-all"
          >
            <CreditCard className="w-5 h-5" />
            שלם את הכל · {totalSupplier} ש״ח
          </button>
          <p className="text-center text-[10px] text-stone-400 mt-2">
            סה״כ נגבה {totalCash} ש״ח · שלך {orders.length * SHIPPING_REWARD_PER_DELIVERY} ש״ח
          </p>
        </div>
      </div>
    </div>
  );
};



const SettlementModal = ({ onClose, onDone, orders, cashCollected, shippingReward, toSupplier, paymentConfig, staffName }) => {
  const [method, setMethod] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const cfg = paymentConfig || {};
  const bossPhoneIntl = `972${normalizePhone2(cfg.boss_whatsapp || '0504944192')}`;
  const orderIds = orders.map((o) => o.id);

  const openLink = (url) => {
    if (!url) return;
    try { window.open(url, '_blank', 'noopener'); } catch (_e) {}
  };

  // Smart "open native app" helper.
  // BIT and PayBox publish no public URL scheme that accepts a payment
  // amount, so the best we can do is force-launch the installed app via
  // either iOS Universal Links or Android Intents. Falls back to the
  // Play Store / web page when the app isn't installed.
  const openNativeApp = (kind) => {
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);

    const apps = {
      bit: {
        ios: 'https://www.bit.co.il/',
        intent: 'intent://www.bit.co.il/#Intent;scheme=https;package=com.bnhp.payments.paymentsapp;S.browser_fallback_url=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dcom.bnhp.payments.paymentsapp;end',
        web:    'https://www.bit.co.il/',
        store:  'https://apps.apple.com/il/app/bit-%D7%91%D7%99%D7%98/id1182007739',
      },
      paybox: {
        ios: cfg.paybox_link || 'https://payboxapp.com/',
        intent: `intent://${(cfg.paybox_link || 'https://links.payboxapp.com/').replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.payboxapp;S.browser_fallback_url=${encodeURIComponent(cfg.paybox_link || 'https://payboxapp.com/')};end`,
        web:    cfg.paybox_link || 'https://payboxapp.com/',
        store:  'https://apps.apple.com/il/app/paybox-%D7%AA%D7%A9%D7%9C%D7%95%D7%9E%D7%99%D7%9D-%D7%95%D7%94%D7%A2%D7%91%D7%A8%D7%AA-%D7%9B%D7%A1%D7%A3/id895491053',
      },
      paypal: {
        ios: cfg.paypal_email ? `https://www.paypal.com/myaccount/transfer/external/email/${encodeURIComponent(cfg.paypal_email)}` : 'https://www.paypal.com/',
        intent: `intent://www.paypal.com/#Intent;scheme=https;package=com.paypal.android.p2pmobile;S.browser_fallback_url=https%3A%2F%2Fwww.paypal.com%2F;end`,
        web:    cfg.paypal_email ? `https://www.paypal.com/myaccount/transfer/external/email/${encodeURIComponent(cfg.paypal_email)}` : 'https://www.paypal.com/',
        store:  'https://apps.apple.com/us/app/paypal-mobile-cash/id283646709',
      },
    };

    const target = apps[kind];
    if (!target) return;

    if (isAndroid) {
      // Android intents - browser will resolve the installed app if any
      // and fall back to the Play Store via the embedded fallback URL.
      window.location.href = target.intent;
    } else if (isIOS) {
      // iOS uses Universal Links - same https URL opens the app when
      // the device has it installed.
      window.location.href = target.ios;
    } else {
      window.open(target.web, '_blank', 'noopener');
    }
  };

  const handleConfirm = async (overrideMethod) => {
    const m = overrideMethod || method;
    if (!m || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/team/settle`, {
        order_ids: orderIds,
        method: m,
        amount: toSupplier,
        settled_by: staffName,
      });
      const def = METHOD_DEFS.find((d) => d.id === m);
      const msg = `שלום, העברתי לך את הסכום ${toSupplier} ש"ח באמצעות ${def?.hebLabel || m} עבור ${orders.length} משלוחים. (${staffName})`;
      const waUrl = `whatsapp://send?phone=${bossPhoneIntl}&text=${encodeURIComponent(msg)}`;
      window.location.href = waUrl;
      setTimeout(() => onDone && onDone(), 600);
    } catch (e) {
      alert('שגיאה: ' + (e.response?.data?.detail || e.message));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="settle-modal">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-l from-emerald-700 to-green-600 text-white p-5 rounded-t-3xl flex items-center justify-between z-10">
          <div>
            <p className="text-white/80 text-xs font-medium">סיכום סופי לפני תשלום</p>
            <h2 className="text-xl font-black">סגירת משלוחים ({orders.length})</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center font-bold" data-testid="close-settle-modal">✕</button>
        </div>

        {/* Summary */}
        <div className="p-5 border-b border-stone-100">
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <p className="text-[10px] text-stone-500">משלוחים נמסרו</p>
              <p className="text-2xl font-black text-green-700">{orders.length}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <p className="text-[10px] text-stone-500">סה״כ נגבה</p>
              <p className="text-2xl font-black text-blue-700">{cashCollected} ש״ח</p>
            </div>
            <div className="bg-stone-50 rounded-xl p-3 text-center">
              <p className="text-[10px] text-stone-500">שייך לי (משלוחים)</p>
              <p className="text-xl font-black text-stone-700">{shippingReward} ש״ח</p>
            </div>
            <div className="bg-amber-100 border-2 border-amber-400 rounded-xl p-3 text-center">
              <p className="text-[10px] text-amber-700">לבריאות הגבר</p>
              <p className="text-2xl font-black text-amber-800" data-testid="settle-supplier-amount">{toSupplier} ש״ח</p>
            </div>
          </div>
        </div>

        {/* Method picker / per-method screen */}
        {!method ? (
          <div className="p-5">
            <p className="text-center text-stone-600 text-sm mb-4 font-bold">איך נוח לך להעביר תשלום של {toSupplier} ש״ח?</p>
            <div className="grid grid-cols-1 gap-2">
              {METHOD_DEFS.map((m) => {
                const Icon = m.icon;
                return (
                  <button
                    key={m.id}
                    onClick={() => setMethod(m.id)}
                    className={`bg-gradient-to-l ${m.color} text-white rounded-2xl py-4 px-5 font-black text-base shadow-md flex items-center justify-between active:scale-[0.98] transition-transform`}
                    data-testid={`pick-method-${m.id}`}
                  >
                    <span className="flex items-center gap-3">
                      <Icon className="w-6 h-6" />
                      {m.label}
                    </span>
                    <ArrowLeft className="w-5 h-5 opacity-80" />
                  </button>
                );
              })}
            </div>
          </div>
        ) : method === 'bank' ? (
          <BankTransferPanel
            cfg={cfg}
            amount={toSupplier}
            onBack={() => setMethod(null)}
            onConfirm={() => handleConfirm('bank')}
            submitting={submitting}
          />
        ) : method === 'cash' ? (
          <CashPanel
            amount={toSupplier}
            onBack={() => setMethod(null)}
            onConfirm={() => handleConfirm('cash')}
            submitting={submitting}
          />
        ) : (
          <WalletPanel
            method={method}
            cfg={cfg}
            amount={toSupplier}
            onOpenApp={() => openNativeApp(method)}
            onBack={() => setMethod(null)}
            onConfirm={() => handleConfirm(method)}
            submitting={submitting}
          />
        )}
      </div>
    </div>
  );
};

// Standard wallet panel for BIT / PayPal / PayBox - amount + recipient with
// copy pills and an "open app" deep-link, plus the WhatsApp confirm CTA.
const WalletPanel = ({ method, cfg, amount, onOpenApp, onBack, onConfirm, submitting }) => {
  const def = METHOD_DEFS.find((m) => m.id === method);
  const recipientLabel = method === 'bit'    ? 'מספר טלפון לביט'
                       : method === 'paypal' ? 'אימייל PayPal'
                       :                       'מזהה פייבוקס';
  const recipientValue = method === 'bit'    ? (cfg.bit_phone || cfg.paybox_phone || '')
                       : method === 'paypal' ? (cfg.paypal_email || '')
                       :                       (cfg.paybox_link || cfg.paybox_phone || '');
  const Icon = def?.icon || Wallet;

  return (
    <div className="p-5 space-y-4">
      <button onClick={onBack} className="text-stone-500 text-sm font-bold flex items-center gap-1 hover:text-stone-800" data-testid="settle-back">
        <ArrowLeft className="w-4 h-4 rotate-180" />
        חזרה
      </button>

      {/* Amount card with copy */}
      <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 text-center">
        <p className="text-stone-500 text-xs mb-1">הסכום שיש להעביר</p>
        <p className="text-4xl font-black text-amber-800" data-testid="wallet-amount">{amount} ש״ח</p>
        <div className="mt-2 flex justify-center">
          <CopyPill value={amount} label="העתק סכום" testid="copy-amount" />
        </div>
      </div>

      {/* Recipient card with copy */}
      <div className="bg-stone-50 rounded-2xl p-4">
        <p className="text-[11px] text-stone-500 mb-1.5 font-bold">{recipientLabel}</p>
        <div className="flex items-center justify-between gap-2">
          <p className="text-base font-black text-stone-800 break-all" dir={method === 'paypal' ? 'ltr' : 'auto'} data-testid="wallet-recipient">
            {recipientValue || '—'}
          </p>
          {recipientValue ? <CopyPill value={recipientValue} label="העתק" testid="copy-recipient" /> : null}
        </div>
      </div>

      {/* Open app deep link */}
      <button
        type="button"
        onClick={onOpenApp}
        className={`w-full bg-gradient-to-l ${def?.color || 'from-blue-500 to-blue-700'} text-white rounded-2xl py-4 font-black text-base shadow-md flex items-center justify-center gap-2 active:scale-[0.98] transition-transform`}
        data-testid="wallet-open-app"
      >
        <Icon className="w-5 h-5" />
        פתח את {def?.hebLabel}
      </button>

      {/* Final confirm CTA */}
      <button
        onClick={onConfirm}
        disabled={submitting}
        className="w-full bg-green-500 hover:bg-green-600 disabled:bg-stone-300 text-white rounded-2xl py-4 font-black text-base shadow-md flex items-center justify-center gap-2"
        data-testid="settle-confirm"
      >
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        סיימתי לשלם — שלח אישור בוואטסאפ
      </button>
      <p className="text-[11px] text-stone-400 text-center">תיפתח שיחת WhatsApp עם הודעת אישור מוכנה לבוס</p>
    </div>
  );
};

// Dedicated bank-transfer panel (matches the customer-facing bank page).
const BankTransferPanel = ({ cfg, amount, onBack, onConfirm, submitting }) => {
  const fields = [
    { key: 'bank_name',    label: 'שם הבנק',    value: cfg.bank_name },
    { key: 'bank_account', label: 'מספר חשבון', value: cfg.bank_account },
    { key: 'bank_branch',  label: 'סניף',       value: cfg.bank_branch },
    { key: 'bank_holder',  label: 'על שם',      value: cfg.bank_holder },
  ];

  return (
    <div className="p-5 space-y-4" data-testid="bank-panel">
      <button onClick={onBack} className="text-stone-500 text-sm font-bold flex items-center gap-1 hover:text-stone-800" data-testid="settle-back">
        <ArrowLeft className="w-4 h-4 rotate-180" />
        חזרה
      </button>

      {/* Green hero with amount */}
      <div className="bg-gradient-to-b from-emerald-600 to-green-700 text-white rounded-2xl p-6 text-center">
        <Building2 className="w-10 h-10 mx-auto mb-2 text-white/95" />
        <h3 className="text-xl font-black">העברה בנקאית</h3>
        <div className="mt-3 inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-2">
          <span className="text-sm font-bold text-white/90">סכום לתשלום:</span>
          <span className="text-lg font-black">{amount} ש״ח</span>
          <CopyPill value={amount} label="העתק סכום" testid="copy-bank-amount" />
        </div>
      </div>

      {/* 4 fields with copy buttons each */}
      <div className="space-y-2">
        {fields.map((f) => (
          <div key={f.key} className="bg-stone-50 rounded-2xl p-4 flex items-center justify-between gap-2" data-testid={`bank-field-${f.key}`}>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-stone-500 mb-0.5 font-bold">{f.label}</p>
              <p className="text-base font-black text-stone-800 break-words">{f.value || '—'}</p>
            </div>
            {f.value ? <CopyPill value={f.value} label={`העתק ${f.label}`} testid={`copy-${f.key}`} /> : null}
          </div>
        ))}
      </div>

      <p className="text-[12px] text-stone-500 text-center bg-amber-50 border border-amber-200 rounded-xl p-3 leading-relaxed">
        חשוב: בהערות ההעברה יש לציין שם מלא והזמנה כדי שנזהה את התשלום
      </p>

      <button
        onClick={onConfirm}
        disabled={submitting}
        className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-stone-300 text-white rounded-2xl py-4 font-black text-base shadow-md flex items-center justify-center gap-2"
        data-testid="settle-confirm"
      >
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        ביצעתי את ההעברה — שלח אישור בוואטסאפ
      </button>
    </div>
  );
};

// Cash panel - the courier just hands the money in person to whoever
// delivers him new stock; we just need a confirmation back.
const CashPanel = ({ amount, onBack, onConfirm, submitting }) => (
  <div className="p-5 space-y-4" data-testid="cash-panel">
    <button onClick={onBack} className="text-stone-500 text-sm font-bold flex items-center gap-1 hover:text-stone-800" data-testid="settle-back">
      <ArrowLeft className="w-4 h-4 rotate-180" />
      חזרה
    </button>

    <div className="bg-gradient-to-b from-emerald-500 to-emerald-700 text-white rounded-2xl p-6 text-center">
      <Coins className="w-12 h-12 mx-auto mb-2 text-white/95" />
      <h3 className="text-xl font-black mb-2">תשלום במזומן</h3>
      <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-2 mb-3">
        <span className="text-sm font-bold text-white/90">הסכום:</span>
        <span className="text-xl font-black">{amount} ש״ח</span>
      </div>
      <p className="text-white/95 text-sm leading-relaxed">
        המסירה במזומן מתבצעת בעת קבלת מלאי חדש —<br />
        תמסור את הסכום לשליח שיביא לך את הסחורה.
      </p>
    </div>

    <button
      onClick={onConfirm}
      disabled={submitting}
      className="w-full bg-green-500 hover:bg-green-600 disabled:bg-stone-300 text-white rounded-2xl py-4 font-black text-base shadow-md flex items-center justify-center gap-2"
      data-testid="settle-confirm"
    >
      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
      מסרתי את המזומן — שלח אישור בוואטסאפ
    </button>
    <p className="text-[11px] text-stone-400 text-center">תיפתח שיחת WhatsApp עם הודעת אישור מוכנה לבוס</p>
  </div>
);

// ---------------------------------------------------------------------------
// Payment-history modal - ledger of every settlement made so far for this
// courier, grouped by date, plus a "make another payment" button so they
// can immediately settle any new completed-but-unpaid orders.
// ---------------------------------------------------------------------------

const PaymentHistoryModal = ({ onClose, settledOrders, completedOrders, onPayMore, onAfterUndo }) => {
  // Group settlements by their `settled_at` event - all orders settled in
  // the same call share the same timestamp, so they form a single payment
  // batch in the user's mind ("שילם 419 ש״ח ב-23.4 בביט").
  const groups = {};
  settledOrders.forEach((o) => {
    const key = (o.settled_at || '').slice(0, 19); // minute precision
    if (!groups[key]) {
      groups[key] = { date: o.settled_at, method: o.settled_method, amount: 0, orders: [] };
    }
    groups[key].amount += Number(o.settled_amount) || 0;
    groups[key].orders.push(o);
  });
  const groupArr = Object.values(groups).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const grandTotal = settledOrders.reduce((s, o) => s + (Number(o.settled_amount) || 0), 0);

  // Undo a wrongly-recorded settlement. Reverts the orders back to "completed"
  // (delivered but unpaid) so the courier owes that money again.
  const undoGroup = async (g) => {
    const ok = window.confirm(
      `לבטל את התשלום של ${g.amount} ש״ח (${METHOD_LABELS[g.method] || g.method})?\n\n` +
      `${g.orders.length} משלוחים יחזרו לטאב "הושלמו" וייחשבו כלא משולמים.`
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/team/unsettle`, { order_ids: g.orders.map(o => o.id) });
      onAfterUndo && onAfterUndo();
      onClose();
    } catch (e) {
      alert('שגיאה: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="payment-history-modal">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-l from-emerald-700 to-green-600 text-white p-5 rounded-t-3xl flex items-center justify-between z-10">
          <div>
            <p className="text-white/80 text-xs font-medium">היסטוריית תשלומים</p>
            <h2 className="text-xl font-black">שולמו עד כה: {grandTotal} ש״ח</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center font-bold" data-testid="close-history">✕</button>
        </div>

        <div className="p-5 space-y-3">
          {groupArr.length === 0 ? (
            <p className="text-center text-stone-500 text-sm py-8">עוד לא בוצעו תשלומים</p>
          ) : (
            groupArr.map((g, i) => {
              const dt = g.date ? new Date(g.date) : null;
              const dateStr = dt ? dt.toLocaleDateString('he-IL', { day:'2-digit', month:'2-digit', year:'numeric' }) : '';
              const timeStr = dt ? dt.toLocaleTimeString('he-IL', { hour:'2-digit', minute:'2-digit' }) : '';
              return (
                <div key={i} className="border-2 border-emerald-200 bg-emerald-50/50 rounded-2xl p-4" data-testid={`history-row-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-2xl font-black text-emerald-700">{g.amount} ש״ח</p>
                      <p className="text-[11px] text-stone-500 mt-0.5">{g.orders.length} משלוחים</p>
                    </div>
                    <div className="text-left">
                      <span className="inline-block bg-emerald-600 text-white text-[11px] font-bold rounded-full px-3 py-1">
                        {METHOD_LABELS[g.method] || g.method}
                      </span>
                      <p className="text-[11px] text-stone-500 mt-1">{dateStr} · {timeStr}</p>
                    </div>
                  </div>
                  <div className="text-[11px] text-stone-600 mt-2 pt-2 border-t border-emerald-100 flex items-center justify-between gap-2">
                    <span className="flex-1">
                      {g.orders.slice(0, 4).map((o) => o.customer_name || '-').join(' · ')}
                      {g.orders.length > 4 ? ` ועוד ${g.orders.length - 4}…` : ''}
                    </span>
                    {/* Undo button - reverts the settlement when the courier
                        accidentally hit "סיימתי לשלם". */}
                    <button
                      type="button"
                      onClick={() => undoGroup(g)}
                      className="flex items-center gap-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md px-2 py-1 text-[11px] font-bold transition-colors"
                      data-testid={`undo-settlement-${i}`}
                      title="בטל את התשלום הזה (אם הזין בטעות)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      ביטול
                    </button>
                  </div>
                </div>
              );
            })
          )}

          {/* CTA - "make another payment". Only enabled when there are
              completed-but-unsettled orders waiting. */}
          <button
            onClick={() => { onClose(); setTimeout(() => onPayMore && onPayMore(), 200); }}
            disabled={!completedOrders || completedOrders.length === 0}
            className="w-full mt-4 bg-gradient-to-l from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 disabled:from-stone-300 disabled:to-stone-300 disabled:cursor-not-allowed text-white rounded-2xl py-4 font-black text-base shadow-md flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            data-testid="history-pay-more"
          >
            <CreditCard className="w-5 h-5" />
            {completedOrders && completedOrders.length > 0
              ? `העבר תשלום נוסף · ${completedOrders.length} משלוחים ממתינים`
              : 'אין משלוחים נוספים לתשלום כרגע'}
          </button>
        </div>
      </div>
    </div>
  );
};



// ── CourierStockManager ────────────────────────────────────────────────────
// Lets Bari set the stock value for the other three couriers from his own
// app. Each card shows the courier's name, the value Bari last saved, and
// an inline input + ✓ save button. Saving fires
//   POST /api/team/stock-override  { courier_name, stock }
// then calls onSaved so the parent re-fetches and the new value also
// reflects immediately if Bari opens that courier's app on another device.
// May 2026.
const CourierStockManager = ({ overrides, productStock, onSaved }) => {
  const COURIERS = ['יניב', 'לירן', 'דניאל'];
  const ROWS = [
    { key: '_total', label: 'סה"כ מלאי' },
    ...STOCK_PRODUCTS.map((p) => ({ key: p.key, label: p.label })),
  ];
  const [drafts, setDrafts] = useState({}); // {"name|key": stringValue}
  const [saving, setSaving] = useState(null);

  const current = (name, key) => {
    if (key === '_total') {
      const o = overrides.find((x) => x.courier_name === name);
      return o ? String(o.base_stock) : '';
    }
    const o = (productStock || []).find((x) => x.courier_name === name && x.product_key === key);
    return o ? String(o.base_stock) : '';
  };

  const valueFor = (name, key) => {
    const dk = `${name}|${key}`;
    return drafts[dk] !== undefined ? drafts[dk] : current(name, key);
  };

  const save = async (name, key) => {
    const dk = `${name}|${key}`;
    const raw = drafts[dk];
    if (raw === undefined || raw === '') return;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0) return;
    setSaving(dk);
    try {
      if (key === '_total') {
        await axios.post(`${API}/team/stock-override`, { courier_name: name, stock: n, updated_by: 'ברי' });
      } else {
        await axios.post(`${API}/team/product-stock`, { courier_name: name, product_key: key, stock: n, updated_by: 'ברי' });
      }
      setDrafts((prev) => { const c = { ...prev }; delete c[dk]; return c; });
      onSaved && onSaved();
    } catch (_e) {
      alert('שגיאה בשמירה — נסה שוב');
    } finally {
      setSaving(null);
    }
  };

  return (
    <section className="mx-4 mt-4 bg-white rounded-2xl shadow-md border border-stone-200 overflow-hidden" data-testid="bari-stock-manager">
      <header className="bg-gradient-to-l from-purple-700 to-indigo-700 text-white px-4 py-2.5 flex items-center gap-2">
        <Package className="w-4 h-4" />
        <h2 className="text-sm font-black tracking-wide">שינוי מלאי לאנשי הצוות</h2>
      </header>
      <div className="divide-y divide-stone-200">
        {COURIERS.map((name) => (
          <div key={name} className="px-4 py-3" data-testid={`stock-courier-${name}`}>
            <p className="font-black text-stone-800 mb-2">{name}</p>
            <div className="space-y-2">
              {ROWS.map((row) => {
                const dk = `${name}|${row.key}`;
                const value = valueFor(name, row.key);
                const isDirty = drafts[dk] !== undefined;
                return (
                  <div key={row.key} className="flex items-center gap-2" data-testid={`stock-row-${name}-${row.key}`}>
                    <p className={`flex-1 min-w-0 text-sm truncate ${row.key === '_total' ? 'font-black text-indigo-800' : 'font-bold text-stone-600'}`}><bdi>{row.label}</bdi></p>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={value}
                      onChange={(e) => setDrafts((p) => ({ ...p, [dk]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') save(name, row.key); }}
                      placeholder="מלאי"
                      className="w-20 px-2 py-1.5 border-2 border-stone-200 focus:border-indigo-500 rounded-lg text-center font-bold text-stone-800 outline-none"
                      data-testid={`stock-input-${name}-${row.key}`}
                    />
                    <button
                      type="button"
                      onClick={() => save(name, row.key)}
                      disabled={!isDirty || saving === dk}
                      className={`p-2 rounded-lg font-bold transition-colors ${isDirty ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-stone-100 text-stone-300 cursor-not-allowed'}`}
                      data-testid={`stock-save-${name}-${row.key}`}
                      aria-label={`שמור ${row.label} ל-${name}`}
                    >
                      {saving === dk ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="px-4 py-2 text-[10px] text-stone-500 bg-stone-50 border-t border-stone-200 text-center">
        השליח רואה את המלאי שהוגדר פחות הזמנות שסיים מאז — לכל מוצר בנפרד.
      </p>
    </section>
  );
};
